import { defineMessages } from 'react-intl-next';

import { MergeCheckKey } from 'src/types';

export const messages = defineMessages({
  sectionAriaLabel: {
    id: 'frontbucket.repository.pullrequest.forgeMergeChecksStatus.sectionAriaLabel',
    description: 'Section name aria label',
    defaultMessage: 'Merge checks status',
  },
  checksCount: {
    id: 'frontbucket.repository.pullrequest.forgeMergeChecksStatus.checksCount',
    description: 'N of M merge checks passed in the current pull request',
    defaultMessage: '{total, plural, =0 {0} other {{resolved} of {total}}}',
  },
  checksLabel: {
    id: 'frontbucket.repository.pullrequest.forgeMergeChecksStatus.checksLabel',
    description: 'Label for merge checks',
    defaultMessage: '{total, plural, one {check} other {checks}}',
  },
  emptyChecks: {
    id: 'frontbucket.repository.pullrequest.forgeMergeChecksStatus.emptyChecks',
    description: 'No merge checks in the current pull request, e.g. 0 checks',
    defaultMessage: '{checksCount} {checksLabel}',
  },
  checksPassed: {
    id: 'frontbucket.repository.pullrequest.forgeMergeChecksStatus.checksPassed',
    description:
      'Checks passed in the current pull request, e.g. <1 of 1> <check> passed, <1 of 2> <checks> passed',
    defaultMessage: '{checksCount} {checksLabel} passed',
  },
  errorText: {
    id: 'frontbucket.repository.pullrequest.forgeMergeChecksStatus.errorText',
    description: 'Text for error while fetching merge checks',
    defaultMessage: 'Failed to fetch latest merge check results',
  },
  checksIconLabel: {
    id: 'frontbucket.repository.pullrequest.forgeMergeChecksStatus.checksIconLabel',
    description: 'Icon label for merge checks',
    defaultMessage: 'Merge checks',
  },
  tryAgain: {
    id: 'frontbucket.repository.pullrequest.forgeMergeChecksStatus.tryAgain',
    description: 'Try again action label',
    defaultMessage: 'Try again',
  },
  allLabel: {
    id: 'frontbucket.repository.pullrequest.mergeChecklist.allLabel',
    description: 'This is used for `All` label in merge check messages',
    defaultMessage: 'All',
  },
  noLabel: {
    id: 'frontbucket.repository.pullrequest.mergeChecklist.noLabel',
    description: 'This is used for `No` label in merge check messages',
    defaultMessage: 'No',
  },
});

export const classicCheckMessages = {
  [MergeCheckKey.MINIMUM_APPROVALS]: defineMessages({
    name: {
      id: 'frontbucket.repository.pullrequest.mergeChecklist.mergeCheck.minimumApprovals',
      description: 'Text for minimum approvals merge check',
      defaultMessage:
        '{count, plural, one {{label} approval} other {{label} approvals}}',
    },
    error: {
      id: 'frontbucket.repository.pullrequest.forgeMergeChecks.mergeCheckItem.minimumApprovals',
      description: 'Text for minimum approvals merge check',
      defaultMessage: 'There are not enough approvals.',
    },
  }),
  [MergeCheckKey.MINIMUM_DEFAULT_REVIEWER_APPROVALS]: defineMessages({
    name: {
      id: 'frontbucket.repository.pullrequest.mergeChecklist.mergeCheck.minimumDefaultReviewerApprovals',
      description: 'Text for minimum default reviewer approvals merge check',
      defaultMessage:
        '{count, plural, one {{label} approval from default reviewers} other {{label} approvals from default reviewers}}',
    },
    error: {
      id: 'frontbucket.repository.pullrequest.forgeMergeChecks.mergeCheckItem.minimumDefaultReviewerApprovals',
      description: 'Text for minimum default reviewer approvals merge check',
      defaultMessage: 'There are not enough approvals from default reviewers.',
    },
  }),
  [MergeCheckKey.REVIEW_GROUPS]: defineMessages({
    name: {
      id: 'frontbucket.repository.pullrequest.mergeChecklist.mergeCheck.reviewGroups',
      description: 'Ensure that all Review Groups are satisified',
      defaultMessage: '{label} Review Groups approved',
    },
    error: {
      id: 'frontbucket.repository.pullrequest.forgeMergeChecks.mergeCheckItem.reviewGroups',
      description: 'Ensure that all Review Groups are satisified',
      defaultMessage: 'There are not enough approvals from review groups.',
    },
  }),
  [MergeCheckKey.MAXIMUM_COMMITS_BEHIND]: defineMessages({
    name: {
      id: 'frontbucket.repository.pullrequest.mergeChecklist.mergeCheck.maximumCommitsBehind',
      description: 'Text for maximum number of commits behind merge check',
      defaultMessage:
        '{count, plural, =0 {No commits behind the destination branch} one {Up to {label} commit behind the destination branch} other {Up to {label} commits behind the destination branch}}',
    },
    error: {
      id: 'frontbucket.repository.pullrequest.forgeMergeChecks.mergeCheckItem.maximumCommitsBehind',
      description: 'Text for maximum number of commits behind merge check',
      defaultMessage:
        'There are too many commits behind the destination branch.',
    },
  }),
  [MergeCheckKey.MINIMUM_SUCCESSFUL_BUILDS]: defineMessages({
    name: {
      id: 'frontbucket.repository.pullrequest.mergeChecklist.mergeCheck.minimumSuccessfulBuilds',
      description: 'Text for minimum successful builds merge check',
      defaultMessage:
        '{count, plural, one {{label} build passed} other {{label} builds passed}}',
    },
    error: {
      id: 'frontbucket.repository.pullrequest.forgeMergeChecks.mergeCheckItem.minimumSuccessfulBuilds',
      description: 'Text for minimum successful builds merge check',
      defaultMessage: 'There are not enough successful builds.',
    },
  }),
  [MergeCheckKey.FAILED_BUILDS]: defineMessages({
    name: {
      id: 'frontbucket.repository.pullrequest.mergeChecklist.mergeCheck.failedBuilds',
      description: 'Text for failed builds merge check',
      defaultMessage: '{label} failed builds',
    },
    error: {
      id: 'frontbucket.repository.pullrequest.forgeMergeChecks.mergeCheckItem.failedBuilds',
      description: 'Text for failed builds merge check',
      defaultMessage: 'There are failed builds that need to pass.',
    },
  }),
  [MergeCheckKey.RESOLVED_TASKS]: defineMessages({
    name: {
      id: 'frontbucket.repository.pullrequest.mergeChecklist.mergeCheck.resolvedTasks',
      description: 'Text for resolved tasks merge check',
      defaultMessage: '{label} tasks resolved',
    },
    error: {
      id: 'frontbucket.repository.pullrequest.forgeMergeChecks.mergeCheckItem.resolvedTasks',
      description: 'Text for resolved tasks merge check',
      defaultMessage: 'There are outstanding tasks that need to be resolved.',
    },
  }),
  [MergeCheckKey.NO_CHANGES_REQUESTED]: defineMessages({
    name: {
      id: 'frontbucket.repository.pullrequest.mergeChecklist.mergeCheck.noChangesRequested',
      description: 'Text for changes requested merge check',
      defaultMessage: 'No changes requested',
    },
    error: {
      id: 'frontbucket.repository.pullrequest.forgeMergeChecks.mergeCheckItem.noChangesRequested',
      description: 'Text for changes requested merge check',
      defaultMessage: 'There are requested changes that need to be resolved.',
    },
  }),
  [MergeCheckKey.PR_DEPS_MERGED]: defineMessages({
    name: {
      id: 'frontbucket.repository.pullrequest.mergeChecklist.mergeCheck.prDepsMerged',
      description: 'Text for PR deps merged merge check',
      defaultMessage: '{label} dependencies merged',
    },
    error: {
      id: 'frontbucket.repository.pullrequest.forgeMergeChecks.mergeCheckItem.prDepsMerged',
      description: 'Text for PR deps merged merge check',
      defaultMessage: "Some dependencies can't be merged. Review and resolve.",
    },
  }),
  [MergeCheckKey.IN_PROGRESS_BUILDS]: defineMessages({
    name: {
      id: 'frontbucket.repository.pullrequest.mergeChecklist.mergeCheck.inProgressBuilds',
      description: 'Text for in progress builds merge check',
      defaultMessage: '{label} in progress builds',
    },
    error: {
      id: 'frontbucket.repository.pullrequest.forgeMergeChecks.mergeCheckItem.inProgressBuilds',
      description: 'Text for in progress builds merge check',
      defaultMessage: 'There are in progress builds that need to pass.',
    },
  }),
  [MergeCheckKey.NO_RESOLVABLE_COMMENTS]: defineMessages({
    name: {
      id: 'frontbucket.repository.pullrequest.mergeChecklist.mergeCheck.noResolveableComments',
      description: 'Text for no resolvable comments merge check',
      defaultMessage: 'All comments resolved',
    },
    error: {
      id: 'frontbucket.repository.pullrequest.forgeMergeChecks.mergeCheckItem.noResolveableComments',
      description: 'Error message for no resolvable comments merge check',
      defaultMessage: 'There are unresolved comments that need to be resolved.',
    },
  }),
};
